import AccessTime from '@mui/icons-material/AccessTime';
import AddComment from '@mui/icons-material/AddComment';
import PaidOutlined from '@mui/icons-material/PaidOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import { NAVY, RADIUS_CARD, STATUS_GOOD, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';
import textToReactNodes from 'utils/textToReactNodes';

import type { LaneResult, RunStatus } from '../types';
import ToolCallCard from './ToolCallCard';

interface OutputCardProps {
  label: string;
  model: string;
  status: RunStatus;
  result?: LaneResult;
  error?: string;
  /** Ground-truth (the original stored response) is visually distinct and never re-runnable. */
  isGroundTruth?: boolean;
  /** When provided, shows an "append to conversation" action for the rendered assistant turn. */
  onAppendToThread?: () => void;
}

const MetaChip: FC<{ icon: ReactNode; text: string; color: string }> = ({ icon, text, color }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      px: 0.9,
      py: 0.25,
      borderRadius: 999,
      bgcolor: alpha(color, 0.12),
      color,
      fontSize: 11,
      fontWeight: 600,
      fontFamily: 'Jost, sans-serif',
    }}
  >
    {icon}
    {text}
  </Box>
);

/** Renders one model result: markdown content, tool calls, and cost/latency metadata. */
const OutputCard: FC<OutputCardProps> = ({
  label,
  model,
  status,
  result,
  error,
  isGroundTruth = false,
  onAppendToThread,
}) => {
  const hasContent = Boolean(result?.content?.trim());
  const toolCalls = result?.toolCalls ?? [];

  return (
    <Box
      sx={{
        bgcolor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_CARD,
        p: 1.5,
        boxShadow: isGroundTruth ? `inset 0 0 0 2px ${alpha(YELLOW, 0.5)}` : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: 12.5, color: NAVY }}>
            {label}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 11,
              color: alpha(NAVY, 0.55),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {model}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {result?.cost && <MetaChip icon={<PaidOutlined sx={{ fontSize: 13 }} />} text={result.cost} color={NAVY} />}
          {typeof result?.time === 'number' && (
            <MetaChip
              icon={<AccessTime sx={{ fontSize: 13 }} />}
              text={`${result.time.toFixed(1)}s`}
              color={STATUS_GOOD}
            />
          )}
        </Box>
      </Box>

      {status === 'running' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} sx={{ color: NAVY }} />
          <Typography sx={{ fontSize: 12.5, color: alpha(NAVY, 0.6), fontFamily: 'Jost, sans-serif' }}>
            Generando…
          </Typography>
        </Box>
      )}

      {status === 'error' && (
        <Typography sx={{ fontSize: 12.5, color: '#E7233B', fontFamily: 'Jost, sans-serif', whiteSpace: 'pre-wrap' }}>
          {error || 'Error al generar la respuesta.'}
        </Typography>
      )}

      {status !== 'running' && status !== 'error' && (
        <>
          {hasContent && (
            <Typography
              component="div"
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: 13.5,
                lineHeight: 1.5,
                color: NAVY,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {textToReactNodes(result?.content)}
            </Typography>
          )}
          {toolCalls.map((tc) => (
            <ToolCallCard key={tc.id} toolCall={tc} />
          ))}
          {!hasContent && toolCalls.length === 0 && status === 'idle' && (
            <Typography sx={{ fontSize: 12.5, color: alpha(NAVY, 0.4), fontFamily: 'Jost, sans-serif' }}>
              Sin respuesta todavía.
            </Typography>
          )}
          {onAppendToThread && (hasContent || toolCalls.length > 0) && (
            <Button
              size="small"
              startIcon={<AddComment sx={{ fontSize: 15 }} />}
              onClick={onAppendToThread}
              sx={{
                mt: 1,
                textTransform: 'none',
                fontFamily: 'Jost, sans-serif',
                fontSize: 12,
                color: NAVY,
                '&:hover': { bgcolor: alpha(NAVY, 0.05) },
              }}
            >
              Agregar a la conversación
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default OutputCard;
